import React, { useState, useEffect } from 'react';
import { X, Phone, ArrowRight, Crown, Zap, Star } from 'lucide-react';
import { Button } from './ui/button';
import { advisorInfo } from '../data/mock';

const rebateList = [
  { name: 'ARUZ', rebate: 'RM3,000' },
  { name: 'ATIVA', rebate: 'RM2,000' },
  { name: 'MYVI', rebate: 'RM1,000' },
  { name: 'AXIA', rebate: 'RM1,000' },
];

const WelcomeModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const hasSeenModal = localStorage.getItem('hasSeenWelcomeModal');

    if (!hasSeenModal) {
      setTimeout(() => {
        setIsOpen(true);
      }, 1000);
    }

    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('hasSeenWelcomeModal', 'true');
  };

  const handleWhatsApp = () => {
    window.open(`${advisorInfo.whatsappLink}?text=${encodeURIComponent('[Website REBATE] Hi Syasyah! Saya nampak ada rebate & free booking fee. Nak tahu lebih lanjut!')}`, '_blank');
    handleClose();
  };

  const handleViewRebate = () => {
    document.getElementById('rebate')?.scrollIntoView({ behavior: 'smooth' });
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] animate-fadeIn cursor-pointer"
        onClick={handleClose}
        role="button"
        aria-label="Close modal"
        tabIndex={-1}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-[#0D0D0D] rounded-2xl shadow-2xl max-w-sm w-full pointer-events-auto animate-slideUp relative overflow-hidden border border-red-800/40"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          data-testid="welcome-modal"
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            aria-label="Close"
            type="button"
            data-testid="welcome-modal-close"
            className="absolute top-3 right-3 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-all"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Top Banner - Red Gradient */}
          <div className="bg-gradient-to-br from-[#8B0000] via-red-700 to-[#5C0000] text-white px-5 py-5 text-center relative">
            <div className="absolute inset-0 opacity-[0.06]" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23fff' fill-opacity='1'%3E%3Cpath d='M20 18v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z'/%3E%3C/g%3E%3C/svg%3E")`,
            }} />
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 bg-amber-500/20 border border-amber-500/30 text-amber-400 px-3 py-1 rounded-full text-[10px] font-bold mb-3 animate-pulse">
                <Zap className="w-3 h-3" />
                TAWARAN TERHAD
              </div>
              <h2 className="text-2xl md:text-3xl font-black tracking-tight">
                REBATE <span className="text-amber-400">TERKINI</span>
              </h2>
              <div className="flex items-center justify-center gap-2 mt-2">
                <span className="text-white/70 text-[10px] font-semibold flex items-center gap-1">
                  <Star className="w-3 h-3 text-amber-400 fill-current" />
                  2026 NEW STOCK
                </span>
                <span className="text-white/30">|</span>
                <span className="text-white/70 text-[10px] font-semibold flex items-center gap-1">
                  <Crown className="w-3 h-3 text-amber-400" />
                  TOP BOOKING 2024/2025
                </span>
              </div>
            </div>
          </div>

          {/* Rebate List */}
          <div className="px-5 py-4">
            <div className="space-y-2">
              {rebateList.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between px-3 py-2.5 bg-white/[0.04] rounded-xl border border-white/[0.06] hover:bg-white/[0.07] transition-colors"
                >
                  <span className="text-white font-bold text-sm tracking-wide">
                    PERODUA {item.name}
                  </span>
                  <span className="text-amber-400 font-black text-lg">
                    {item.rebate}
                  </span>
                </div>
              ))}
            </div>

            {/* Free Booking Fee */}
            <div className="mt-4 bg-gradient-to-r from-amber-500/10 to-amber-600/10 border border-amber-500/20 rounded-xl py-3 px-4 text-center">
              <p className="text-amber-400/60 text-[10px] font-bold uppercase tracking-[0.2em]">Bonus</p>
              <p className="text-amber-400 text-lg font-black">FREE BOOKING FEE</p>
            </div>

            {/* CTAs */}
            <div className="mt-4 space-y-2.5">
              <Button
                onClick={handleWhatsApp}
                data-testid="welcome-modal-whatsapp"
                className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white font-bold py-4 rounded-xl text-sm shadow-lg transition-all hover:scale-[1.02]"
              >
                <Phone className="w-4 h-4 mr-2" />
                WhatsApp Sekarang
              </Button>

              <Button
                onClick={handleViewRebate}
                variant="outline"
                className="w-full border border-red-700/40 bg-white/[0.03] hover:bg-white/[0.07] text-white/80 font-semibold py-4 rounded-xl text-sm transition-all"
              >
                Lihat Semua Rebate
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.97);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.35s ease-out;
        }
      `}</style>
    </>
  );
};

export default WelcomeModal;
